import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Mail, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const SimpleGmailIntegration = () => {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Simple Gmail connection function
  const connectGmail = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Simple OAuth URL generation - we'll implement this step by step
      const response = await fetch('/api/gmail/connect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          redirect_uri: `${window.location.origin}/auth/callback`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate OAuth URL');
      }

      const data = await response.json();
      
      // Redirect to Google OAuth
      if (data.auth_url) {
        window.location.href = data.auth_url;
      }
    } catch (err) {
      console.error('Gmail connection error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Check connection status on component mount
  useEffect(() => {
    checkGmailStatus();
  }, []);

  const checkGmailStatus = async () => {
    try {
      const response = await fetch('/api/gmail/status', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConnected(data.connected || false);
      }
    } catch (err) {
      console.log('Status check failed:', err);
      setConnected(false);
    }
  };

  const disconnectGmail = async () => {
    if (!confirm('Are you sure you want to disconnect Gmail? This will stop email tracking.')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/gmail/disconnect', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to disconnect Gmail');
      }

      setConnected(false);
      setError(null);
    } catch (err) {
      console.error('Disconnect error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Mail className="w-5 h-5" />
          <span>Gmail Integration</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Gmail Account</p>
              <p className="text-sm text-gray-600">
                {connected ? 'Connected and ready' : 'Not connected'}
              </p>
            </div>
          </div>

          {connected ? (
            <Badge className="bg-green-100 text-green-800">
              <CheckCircle className="w-3 h-3 mr-1" />
              Connected
            </Badge>
          ) : (
            <Badge variant="outline" className="text-gray-600">
              Not Connected
            </Badge>
          )}
        </div>

        {!connected && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Connect your Gmail account to automatically track email communications with CRM contacts.
            </p>
            
            <Button 
              onClick={connectGmail} 
              disabled={loading}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Connect Gmail Account
                </>
              )}
            </Button>
          </div>
        )}

        {connected && (
          <div className="space-y-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-green-700 text-sm">
                ✅ Gmail connected successfully! Email tracking is now active.
              </p>
            </div>
            
            <div className="flex space-x-2">
              <Button 
                onClick={disconnectGmail}
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                Disconnect Gmail
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SimpleGmailIntegration;